import * as React from 'react';
import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Map,
  Marker,
  Popup,
  NavigationControl,
  FullscreenControl,
  ScaleControl,
  GeolocateControl
} from '@vis.gl/react-maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Database } from '@/types/supabase';
import { Badge } from '@/components/ui/badge';
import { cn, getStatusColor, formatDate } from '@/lib/utils';
import Pin from './Pin';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useNavigate } from 'react-router-dom';
import { Star, Building2, MapPin, FileText, Image as ImageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

export type BasicAsset = Database['public']['Tables']['assets']['Row'] & {
  company?: {
    id: number;
    name: string;
    description?: string;
  };
  place?: {
    id: number;
    name: string;
    description?: string;
  };
};

type Company = {
  id: number;
  company_name: string;
  description?: string;
  latitude: number;
  longitude: number;
  location?: string;
  created_at: string;
  updated_at: string;
  user_id: string;
};

type Place = {
  id: number;
  place_name: string;
  description?: string;
  latitude: number;
  longitude: number;
  location?: string;
  created_at: string;
  updated_at: string;
  user_id: string;
};

type MapEntity = BasicAsset | Company | Place;

interface AssetsMapProps {
  onAssetClick?: (asset: BasicAsset) => void;
  className?: string;
  assets?: BasicAsset[];
}

interface MapFilters {
  showAssets: boolean;
  showCompanies: boolean;
  showPlaces: boolean;
}

const isValidCoordinate = (value: any): value is number => {
  const num = Number(value);
  return !isNaN(num) && isFinite(num);
};

const parseCoordinates = (entity: any) => {
  if (!entity?.latitude || !entity?.longitude) {
    console.log('Invalid coordinates for entity:', entity);
    return null;
  }
  const lat = Number(entity.latitude);
  const lng = Number(entity.longitude);
  if (isNaN(lat) || isNaN(lng)) {
    console.log('NaN coordinates for entity:', entity);
    return null;
  }
  return { lat, lng };
};

// Define cluster interface
interface Cluster {
  latitude: number;
  longitude: number;
  assets: BasicAsset[];
}

// ClusterPin component
function ClusterPin({ size = 40, count }: { size?: number; count: number }) {
  const pinStyle = {
    cursor: 'pointer',
    background: '#007AFF',
    color: 'white',
    borderRadius: '50%',
    width: `${size}px`,
    height: `${size}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid white',
    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
    fontSize: `${size/2}px`,
    fontWeight: 'bold',
  };

  return (
    <div style={pinStyle}>
      {count}
    </div>
  );
}

// Function to calculate distance between two points
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = lat1 * Math.PI/180;
  const φ2 = lat2 * Math.PI/180;
  const Δφ = (lat2-lat1) * Math.PI/180;
  const Δλ = (lon2-lon1) * Math.PI/180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c;
};

// Helper function to determine entity type
const isAsset = (entity: MapEntity): entity is BasicAsset => 'asset_name' in entity;
const isCompany = (entity: MapEntity): entity is Company => 'company_name' in entity;
const isPlace = (entity: MapEntity): entity is Place => 'place_name' in entity;

export function AssetsMap({ onAssetClick, className, assets: passedAssets }: AssetsMapProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [popupInfo, setPopupInfo] = useState<MapEntity | null>(null);
  const [clusterPopupInfo, setClusterPopupInfo] = useState<Cluster | null>(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(3);
  const [filters, setFilters] = useState<MapFilters>({
    showAssets: true,
    showCompanies: true,
    showPlaces: true
  });

  // Add userCompanies and userPlaces queries
  const { data: userCompanies } = useQuery<Company[]>({
    queryKey: ['userCompanies'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // First get companies owned by user
      const { data: ownedCompanies, error: ownedError } = await supabase
        .from('companies')
        .select('*')
        .eq('user_id', user.id)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null);

      if (ownedError) throw ownedError;

      // Then get companies shared with user
      const { data: sharedCompanies, error: sharedError } = await supabase
        .from('shared_assets')
        .select(`
          company:companies(*)
        `)
        .eq('shared_with', user.id)
        .eq('shared_type', 'company')
        .not('company.latitude', 'is', null)
        .not('company.longitude', 'is', null);

      if (sharedError) throw sharedError;

      const owned = ownedCompanies.map(company => ({
        ...company,
        company_name: company.name
      }));

      const shared = sharedCompanies
        .map(share => share.company)
        .filter((company): company is NonNullable<typeof company> => company !== null)
        .map(company => ({
          ...company,
          company_name: company.name
        }));

      return [...owned, ...shared] as Company[];
    },
  });

  const { data: userPlaces } = useQuery<Place[]>({
    queryKey: ['userPlaces'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // First get places owned by user
      const { data: ownedPlaces, error: ownedError } = await supabase
        .from('places')
        .select('*')
        .eq('user_id', user.id)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null);

      if (ownedError) throw ownedError;

      // Then get places shared with user
      const { data: sharedPlaces, error: sharedError } = await supabase
        .from('shared_assets')
        .select(`
          place:places(*)
        `)
        .eq('shared_with', user.id)
        .eq('shared_type', 'place')
        .not('place.latitude', 'is', null)
        .not('place.longitude', 'is', null);

      if (sharedError) throw sharedError;

      const owned = ownedPlaces.map(place => ({
        ...place,
        place_name: place.name
      }));

      const shared = sharedPlaces
        .map(share => share.place)
        .filter((place): place is NonNullable<typeof place> => place !== null)
        .map(place => ({
          ...place,
          place_name: place.name
        }));

      return [...owned, ...shared] as Place[];
    },
  });

  // Add debug useEffect
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('AssetsMap mounted with:', {
        assets: passedAssets?.length,
        companies: userCompanies?.length,
        places: userPlaces?.length
      });
    }
    setLoading(false);
  }, [passedAssets, userCompanies, userPlaces]);

  // Function to create clusters based on zoom level and proximity
  const createClusters = useCallback((assets: BasicAsset[], zoom: number): (Cluster | BasicAsset)[] => {
    if (zoom >= 13) return assets; // Don't cluster at high zoom levels

    const clusters: Cluster[] = [];
    const processed = new Set<number>();

    // Adjust clustering radius based on zoom level (in meters)
    const clusterRadius = Math.max(10000 * Math.pow(2, 8 - zoom), 100);

    assets.forEach((asset, i) => {
      if (processed.has(i)) return;

      const coords = parseCoordinates(asset);
      if (!coords) return;

      const cluster: Cluster = {
        latitude: coords.lat,
        longitude: coords.lng,
        assets: [asset]
      };

      // Find nearby assets
      assets.forEach((otherAsset, j) => {
        if (i === j || processed.has(j)) return;

        const otherCoords = parseCoordinates(otherAsset);
        if (!otherCoords) return;

        const distance = calculateDistance(
          coords.lat,
          coords.lng,
          otherCoords.lat,
          otherCoords.lng
        );

        if (distance <= clusterRadius) {
          cluster.assets.push(otherAsset);
          processed.add(j);
        }
      });

      processed.add(i);

      if (cluster.assets.length > 1) {
        // Calculate cluster center
        const center = cluster.assets.reduce(
          (acc, curr) => {
            const coords = parseCoordinates(curr);
            if (coords) {
              acc.lat += coords.lat;
              acc.lng += coords.lng;
            }
            return acc;
          },
          { lat: 0, lng: 0 }
        );

        cluster.latitude = center.lat / cluster.assets.length;
        cluster.longitude = center.lng / cluster.assets.length;
        clusters.push(cluster);
      } else {
        // Single asset, no need to cluster
        processed.delete(i);
      }
    });

    // Return both clusters and unclustered assets
    return [
      ...clusters,
      ...assets.filter((_, i) => !processed.has(i))
    ];
  }, []);

  // Create markers with filtering
  const markers = useMemo(() => {
    console.log('Creating markers with assets:', passedAssets);
    const allMarkers = [];
    
    // Add clustered asset markers if showAssets is true
    if (filters.showAssets && passedAssets) {
      const items = createClusters(passedAssets, zoom);
      allMarkers.push(...items.map((item, index) => {
        if ('assets' in item) { // It's a cluster
          return (
            <Marker
              key={`cluster-${index}`}
              longitude={item.longitude}
              latitude={item.latitude}
              anchor="center"
              onClick={e => {
                e.originalEvent.stopPropagation();
                setClusterPopupInfo(item);
                setPopupInfo(null);
              }}
            >
              <ClusterPin count={item.assets.length} />
            </Marker>
          );
        } else { // It's a single asset
          const coords = parseCoordinates(item);
          if (!coords) return null;

          return (
            <Marker
              key={`asset-${item.id}`}
              longitude={coords.lng}
              latitude={coords.lat}
              anchor="bottom"
              onClick={e => {
                e.originalEvent.stopPropagation();
                setPopupInfo(item);
                setClusterPopupInfo(null);
                if (onAssetClick && isAsset(item)) {
                  onAssetClick(item);
                }
              }}
            >
              <Pin size={30} category={item.category} />
            </Marker>
          );
        }
      }).filter(Boolean));
    }

    // Add company markers if showCompanies is true
    if (filters.showCompanies && userCompanies) {
      userCompanies.forEach(company => {
        const coords = parseCoordinates(company);
        if (!coords) return;

        allMarkers.push(
          <Marker
            key={`company-${company.id}`}
            longitude={coords.lng}
            latitude={coords.lat}
            anchor="bottom"
            onClick={e => {
              e.originalEvent.stopPropagation();
              setPopupInfo(company as MapEntity);
              setClusterPopupInfo(null);
            }}
          >
            <Pin size={30} category="COMPANY" />
          </Marker>
        );
      });
    }

    // Add place markers if showPlaces is true
    if (filters.showPlaces && userPlaces) {
      userPlaces.forEach(place => {
        const coords = parseCoordinates(place);
        if (!coords) return;

        allMarkers.push(
          <Marker
            key={`place-${place.id}`}
            longitude={coords.lng}
            latitude={coords.lat}
            anchor="bottom"
            onClick={e => {
              e.originalEvent.stopPropagation();
              setPopupInfo(place as MapEntity);
              setClusterPopupInfo(null);
            }}
          >
            <Pin size={30} category="PLACE" />
          </Marker>
        );
      });
    }

    return allMarkers;
  }, [passedAssets, userCompanies, userPlaces, zoom, filters, onAssetClick, createClusters]);

  // Calculate initial view state
  const initialViewState = useMemo(() => {
    if (!passedAssets?.length) {
      return {
        latitude: 25,
        longitude: -100,
        zoom: 3,
        bearing: 0,
        pitch: 0
      };
    }

    const allCoords = passedAssets
      .map(asset => parseCoordinates(asset))
      .filter((coords): coords is { lat: number; lng: number } => coords !== null);

    if (allCoords.length === 0) {
      return {
        latitude: 25,
        longitude: -100,
        zoom: 3,
        bearing: 0,
        pitch: 0
      };
    }

    // Calculate the center of all coordinates
    const center = allCoords.reduce(
      (acc, curr) => {
        acc.lat += curr.lat;
        acc.lng += curr.lng;
        return acc;
      },
      { lat: 0, lng: 0 }
    );

    center.lat /= allCoords.length;
    center.lng /= allCoords.length;

    return {
      latitude: center.lat,
      longitude: center.lng,
      zoom: 5,
      bearing: 0,
      pitch: 0
    };
  }, [passedAssets]);

  // Modify the popup content based on the type of entity
  const renderPopupContent = (info: MapEntity) => {
    // Check if it's a company
    if (isCompany(info)) {
      return (
        <div className="p-4 max-w-md">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="h-4 w-4 text-yellow-500" />
            <span className="text-sm text-gray-500">{t('map.entities.company')}</span>
          </div>
          <h3 className="font-semibold">{info.company_name}</h3>
          {info.description && (
            <p className="text-sm text-gray-600 mt-2">{info.description}</p>
          )}
          <div className="mt-2 text-xs text-gray-500">
            <div className="flex items-center gap-2">
              <MapPin className="h-3 w-3" />
              <span>
                {Number(info.latitude).toFixed(6)}, {Number(info.longitude).toFixed(6)}
              </span>
            </div>
          </div>
          <Button 
            className="w-full mt-4"
            onClick={() => navigate(`/companies/${info.id}`)}
          >
            {t('map.popup.viewCompany')}
          </Button>
        </div>
      );
    }
    
    // Check if it's a place
    if (isPlace(info)) {
      return (
        <div className="p-4 max-w-md">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="h-4 w-4 text-green-500" />
            <span className="text-sm text-gray-500">{t('map.entities.place')}</span>
          </div>
          <h3 className="font-semibold">{info.place_name}</h3>
          {info.description && (
            <p className="text-sm text-gray-600 mt-2">{info.description}</p>
          )}
          <div className="mt-2 text-xs text-gray-500">
            <div className="flex items-center gap-2">
              <MapPin className="h-3 w-3" />
              <span>
                {Number(info.latitude).toFixed(6)}, {Number(info.longitude).toFixed(6)}
              </span>
            </div>
          </div>
          <Button 
            className="w-full mt-4"
            onClick={() => navigate(`/places/${info.id}`)}
          >
            {t('map.popup.viewPlace')}
          </Button>
        </div>
      );
    }

    // If it's an asset, use the existing asset popup content
    if (isAsset(info)) {
      return (
        <div className="p-4 max-w-md">
          <div className="flex items-center gap-2 mb-3">
            <Star className="h-4 w-4 text-blue-500" />
            <span className="text-sm text-gray-500">{t('map.entities.asset')}</span>
          </div>
          <button
            onClick={() => setPopupInfo(null)}
            className="absolute top-2 right-2 p-1 hover:bg-gray-100 rounded-full"
            aria-label={t('map.popup.close')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <div className="space-y-4">
            {/* Header with Name and Status */}
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold">
                {info.name || info.asset_name}
              </h3>
              {info.status && (
                <Badge className={cn(getStatusColor(info.status))}>
                  {info.status}
                </Badge>
              )}
            </div>

            {/* Images Gallery */}
            {info.images && (
              <div className="flex gap-2 overflow-x-auto py-2">
                {info.images.split(',').map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`${t('map.popup.images')} ${index + 1}`}
                    className="h-20 w-20 object-cover rounded-md"
                  />
                ))}
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-2 text-sm">
              {info.category && (
                <div>
                  <span className="text-gray-500">{t('map.popup.category')}:</span> {info.category}
                </div>
              )}
              {info.asset_price && (
                <div>
                  <span className="text-gray-500">{t('map.popup.price')}:</span> ${info.asset_price.toLocaleString()}
                </div>
              )}
              {info.rating !== null && (
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 text-yellow-400" />
                  <span>{t('map.popup.rating')}: {info.rating}/5</span>
                </div>
              )}
              {info.conditions !== null && (
                <div>
                  <span className="text-gray-500">{t('map.popup.condition')}:</span> {info.conditions}/5
                </div>
              )}
            </div>

            {/* Location Info */}
            <div className="space-y-2 text-sm">
              {(info.company || info.place) && (
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-gray-500" />
                  <span>
                    {info.company?.name}
                    {info.company && info.place && ' - '}
                    {info.place?.name}
                  </span>
                </div>
              )}
              {info.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <span>{info.location}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <MapPin className="h-3 w-3" />
                <span>
                  {Number(info.latitude).toFixed(6)}, {Number(info.longitude).toFixed(6)}
                </span>
              </div>
            </div>

            {/* Description and Notes */}
            {(info.asset_description || info.notes) && (
              <div className="space-y-2 text-sm">
                {info.asset_description && (
                  <div>
                    <span className="text-gray-500">{t('map.popup.description')}:</span>
                    <p className="text-gray-600">{info.asset_description}</p>
                  </div>
                )}
                {info.notes && (
                  <div>
                    <span className="text-gray-500">{t('map.popup.notes')}:</span>
                    <p className="text-gray-500 text-xs">{info.notes}</p>
                  </div>
                )}
              </div>
            )}

            {/* Dates */}
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
              {info.purchase_date && (
                <div>
                  <span className="block font-medium">{t('map.popup.purchased')}</span>
                  {formatDate(info.purchase_date)}
                </div>
              )}
              {info.warranty_expiry && (
                <div>
                  <span className="block font-medium">{t('map.popup.warrantyExpires')}</span>
                  {formatDate(info.warranty_expiry)}
                </div>
              )}
            </div>

            {/* Documents and Additional Info */}
            <div className="flex flex-wrap gap-2 text-xs">
              {info.documents && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  {info.documents.split(',').length} {t('map.popup.documents')}
                </Badge>
              )}
              {info.images && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <ImageIcon className="h-3 w-3" />
                  {info.images.split(',').length} {t('map.popup.images')}
                </Badge>
              )}
              {info.private_code && (
                <Badge variant="outline">
                  {t('map.popup.code')}: {info.private_code}
                </Badge>
              )}
            </div>

            {/* Action Button */}
            <Button 
              className="w-full mt-4"
              onClick={() => navigate(`/assets/${info.id}`)}
            >
              {t('map.popup.viewDetails')}
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-[600px]">{t('map.loading')}</div>;
  }

  return (
    <div className={cn("relative w-full h-[600px]", className)}>
      {/* Add Map Filters */}
      <div className="absolute top-4 right-4 z-10 bg-white p-3 rounded-lg shadow-lg space-y-2">
        <div className="text-sm font-medium mb-2">{t('map.filters.title')}</div>
        <div className="space-y-2">
          <label className="flex items-center space-x-2">
            <Checkbox
              checked={filters.showAssets}
              onCheckedChange={(checked) => 
                setFilters(prev => ({ ...prev, showAssets: checked as boolean }))
              }
            />
            <span className="text-sm flex items-center gap-2">
              <Star className="h-4 w-4 text-blue-500" />
              {t('map.filters.assets')}
            </span>
          </label>
          <label className="flex items-center space-x-2">
            <Checkbox
              checked={filters.showCompanies}
              onCheckedChange={(checked) => 
                setFilters(prev => ({ ...prev, showCompanies: checked as boolean }))
              }
            />
            <span className="text-sm flex items-center gap-2">
              <Building2 className="h-4 w-4 text-yellow-500" />
              {t('map.filters.companies')}
            </span>
          </label>
          <label className="flex items-center space-x-2">
            <Checkbox
              checked={filters.showPlaces}
              onCheckedChange={(checked) => 
                setFilters(prev => ({ ...prev, showPlaces: checked as boolean }))
              }
            />
            <span className="text-sm flex items-center gap-2">
              <MapPin className="h-4 w-4 text-green-500" />
              {t('map.filters.places')}
            </span>
          </label>
        </div>
      </div>

      <Map
        initialViewState={initialViewState}
        mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
        style={{ width: '100%', height: '100%' }}
        reuseMaps
        onZoom={e => setZoom(e.viewState.zoom)}
        onClick={() => {
          setPopupInfo(null);
          setClusterPopupInfo(null);
        }}
      >
        <GeolocateControl position="top-left" />
        <FullscreenControl position="top-left" />
        <NavigationControl position="top-left" />
        <ScaleControl />

        {markers}

        {clusterPopupInfo && (
          <Popup
            anchor="top"
            longitude={clusterPopupInfo.longitude}
            latitude={clusterPopupInfo.latitude}
            onClose={() => setClusterPopupInfo(null)}
            closeButton={true}
            closeOnClick={false}
            maxWidth="400px"
            className="custom-popup"
          >
            <div className="p-4">
              <button
                onClick={() => setClusterPopupInfo(null)}
                className="absolute top-2 right-2 p-1 hover:bg-gray-100 rounded-full"
                aria-label={t('map.popup.close')}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
              <h3 className="font-semibold mb-2 pr-8">
                {clusterPopupInfo.assets.length} {t('map.cluster.title')}
              </h3>
              <div className="max-h-[300px] overflow-y-auto space-y-2">
                {clusterPopupInfo.assets.map((asset) => (
                  <div
                    key={asset.id}
                    className="p-2 hover:bg-gray-50 rounded cursor-pointer"
                    onClick={() => {
                      setPopupInfo(asset);
                      setClusterPopupInfo(null);
                    }}
                    title={t('map.cluster.clickToView')}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{asset.name || asset.asset_name}</span>
                      {asset.status && (
                        <Badge className={cn(getStatusColor(asset.status))}>
                          {asset.status}
                        </Badge>
                      )}
                    </div>
                    {asset.category && (
                      <div className="text-sm text-gray-500">{asset.category}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Popup>
        )}

        {popupInfo && (
          <Popup
            anchor="top"
            longitude={Number(popupInfo.longitude)}
            latitude={Number(popupInfo.latitude)}
            onClose={() => setPopupInfo(null)}
            closeButton={true}
            closeOnClick={false}
            maxWidth="400px"
            className="custom-popup"
          >
            {renderPopupContent(popupInfo)}
          </Popup>
        )}
      </Map>
    </div>
  );
} 
